import { FormattedMessage } from "react-intl";

import styled from "styled-components";

import { Card, Empty, Pagination, Skeleton } from "antd";

import { CmtUserCard } from "@app/components/users/CmtUserCard.tsx";
import { USERS_PAGE_SIZE } from "@app/constants/paginationConstants.ts";
import { UsersPage } from "@app/models/User.ts";

const StyledGridContainer = styled.div`
  container-type: inline-size;
`;

// Column counts divide USERS_PAGE_SIZE so a full page has no partial row.
const StyledGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;

  @container (min-width: 560px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @container (min-width: 900px) {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }
`;

const StyledFooter = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2rem;
`;

interface CmtUsersGridProps {
  users?: UsersPage;
  isLoading: boolean;
  currentPage: number;
  onPageChange: (page: number) => void;
}

export const CmtUsersGrid = ({
  users,
  isLoading,
  currentPage,
  onPageChange,
}: CmtUsersGridProps) => {
  if (isLoading) {
    return (
      <StyledGridContainer>
        <StyledGrid>
          {Array.from({ length: USERS_PAGE_SIZE }, (_, index) => (
            <Card key={index} style={{ borderRadius: 16 }}>
              <Skeleton avatar={{ size: 72 }} active paragraph={{ rows: 1 }} />
            </Card>
          ))}
        </StyledGrid>
      </StyledGridContainer>
    );
  }

  if (!users?.content.length) {
    return (
      <Empty
        style={{ marginTop: "4rem" }}
        description={
          <FormattedMessage
            id="users.empty"
            defaultMessage="No employee matches these filters"
          />
        }
      />
    );
  }

  return (
    <StyledGridContainer>
      <StyledGrid>
        {users.content.map((user) => (
          <CmtUserCard key={user.id} user={user} />
        ))}
      </StyledGrid>
      <StyledFooter>
        <Pagination
          current={currentPage}
          pageSize={USERS_PAGE_SIZE}
          total={users.totalElements}
          showSizeChanger={false}
          onChange={onPageChange}
          showTotal={(total) => (
            <FormattedMessage
              id="users.total"
              defaultMessage="{total, plural, one {# employee} other {# employees}}"
              values={{ total }}
            />
          )}
        />
      </StyledFooter>
    </StyledGridContainer>
  );
};
